// src/components/AdminPortal/UserManagement/Applications.js
import React, { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import Axios from "../../../utils/axios";
import ContentLoader from "../../../global/Content/ContentLoader";
import "../UserManagement.css";
import "../../User.css";

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    applicationName: "",
    applicationCode: "",
    description: "",
  });

  // 🔹 Delete modal states
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 🔹 Fetch all applications
  const fetchApplications = async () => {
    try {
      setIsRefreshing(true);
      const response = await Axios.get(`/api/application/getAllApplications`);
      setApplications(response?.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 🔹 Get Application by ID
  const fetchApplicationById = async (id) => {
    try {
      const response = await Axios.get(
        `/api/application/getApplicationById?id=${id}`
      );
      return response?.data;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  // 🔹 Create application
  const createApplication = async (data) => {
    try {
      const response = await Axios.post(
        `/api/application/createApplication`,
        data
      );
      return response?.data;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  // 🔹 Update application
  const updateApplication = async (id, data) => {
    try {
      const response = await Axios.put(
        `/api/application/updateApplication?id=${id}`,
        data
      );
      return response?.data;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    }
  };

  // 🔹 Delete application
  const deleteApplication = async (id) => {
    try {
      await Axios.delete(`/api/application/deleteApplication?id=${id}`);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      return false;
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // 🔹 Filter for search
  const filteredApplications = applications.filter(
    (app) =>
      app.applicationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicationCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 🔹 Validation
  const validateForm = () => {
    let newErrors = {};
    if (!formData.applicationName)
      newErrors.applicationName = "Application Name is required";
    if (!formData.applicationCode)
      newErrors.applicationCode = "Application Code is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 New Application
  const handleNew = () => {
    setCurrentApplication(null);
    setFormData({ applicationName: "", applicationCode: "", description: "" });
    setIsModalOpen(true);
  };

  // 🔹 Edit Application
  const handleEdit = async (application) => {
    const fullApp = await fetchApplicationById(application.id);
    setCurrentApplication(fullApp || application);
    setFormData({
      applicationName:
        fullApp?.applicationName || application.applicationName || "",
      applicationCode:
        fullApp?.applicationCode || application.applicationCode || "",
      description: fullApp?.description || application.description || "",
    });
    setIsModalOpen(true);
  };

  // 🔹 Open Delete Modal
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  // 🔹 Confirm Delete
  const confirmDelete = async () => {
    if (!deleteId) return;

    setIsLoading(true);
    try {
      const success = await deleteApplication(deleteId);
      if (success) {
        setApplications(applications.filter((app) => app.id !== deleteId));
        toast.success("Application deleted successfully!");
      }
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // 🔹 Input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 🔹 Save Application
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (currentApplication) {
        const updated = await updateApplication(
          currentApplication.id,
          formData
        );
        setApplications(
          applications.map((app) =>
            app.id === currentApplication.id ? updated : app
          )
        );
        toast.success("Application updated successfully!");
      } else {
        const created = await createApplication(formData);
        setApplications([...applications, created]);
        toast.success("Application created successfully!");
      }
      setIsModalOpen(false);
    } catch (error) {
      toast.error("Failed to save application");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="table-container">
      <h2 style={{ fontWeight: "700", fontFamily: "unset" }}>Applications</h2>
      <br />
      <div
        className="search-and-new"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flexGrow: 1, marginRight: "10px" }}
        />
        <button
          onClick={() => setSearchTerm("")}
          className="clear-button"
          style={{ marginRight: "10px" }}
        >
          Clear
        </button>
        <button onClick={handleNew} className="new-button" disabled={isLoading}>
          <FiPlus />
        </button>
      </div>

      {isRefreshing && !applications.length ? (
        <div className="loading">
          <ContentLoader />
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Application Name</th>
              <th>Description</th>
              <th>Application Code</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplications.map((application) => (
              <tr key={application.id}>
                <td>{application.applicationName}</td>
                <td>{application.description}</td>
                <td>{application.applicationCode}</td>
                <td className="action-buttons">
                  <button onClick={() => handleEdit(application)} title="Edit">
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(application.id)}
                    title="Delete"
                  >
                    <FiTrash2 className="delete-icon" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 🔹 Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>
                {currentApplication ? "Edit Application" : "New Application"}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Application Name*</label>
                <input
                  type="text"
                  name="applicationName"
                  value={formData.applicationName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.applicationName && (
                  <p className="text-danger">{errors.applicationName}</p>
                )}
              </div>
              <div className="form-group">
                <label>Application Code*</label>
                <input
                  type="text"
                  name="applicationCode"
                  value={formData.applicationCode}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.applicationCode && (
                  <p className="text-danger">{errors.applicationCode}</p>
                )}
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="cancel-button"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="save-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 🔹 Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm Delete</h2>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="modal-close-button"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: "15px", marginBottom: "20px" }}>
                Are you sure you want to delete this application?
              </p>
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="cancel-button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="delete-button"
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
